import type { QueryClient } from '@tanstack/react-query';
import type { RealtimePostgresChangesPayload } from '@supabase/supabase-js';
import { queryKeys } from '../query/queryKeys';

type Row = Record<string, unknown>;

export interface TableConfig {
  /** Realtime row-level filter for a given userId (passed to Supabase channel.on). */
  filter: (userId: string) => string;
  /** Translates a Postgres change event into one or more cache invalidations. */
  invalidate: (
    payload: RealtimePostgresChangesPayload<Row>,
    qc: QueryClient,
  ) => void;
}

/**
 * Realtime DELETE payloads only carry primary-key-ish columns in `old`, so
 * invalidators must tolerate sparse rows. INSERT/UPDATE carry the full row
 * in `new`. Pick whichever side has the data.
 */
function rowOf<T extends Row>(payload: RealtimePostgresChangesPayload<T>): T | null {
  if (payload.eventType === 'DELETE') return (payload.old ?? null) as T | null;
  return (payload.new ?? null) as T | null;
}

/**
 * A bulk write echoes one event per row, and each invalidation would start a
 * fresh read of the whole list. This waits until the echoes stop for `quietMs`,
 * then invalidates once. For tables a single action can write hundreds of rows
 * to (the saved shelf's import); ordinary tables invalidate at once.
 */
const quietTimers = new Map<string, ReturnType<typeof setTimeout>>();
function invalidateAfterQuiet(qc: QueryClient, queryKey: readonly unknown[], quietMs = 400): void {
  const id = JSON.stringify(queryKey);
  clearTimeout(quietTimers.get(id));
  quietTimers.set(
    id,
    setTimeout(() => {
      quietTimers.delete(id);
      qc.invalidateQueries({ queryKey });
    }, quietMs),
  );
}

/**
 * ⚠️ ADDING A TABLE HERE IS NOT ENOUGH — IT MUST ALSO BE IN THE PUBLICATION.
 *
 * `createSupervisorChannel` binds every key below onto ONE Supabase channel. If
 * ANY ONE of them is missing from the `supabase_realtime` publication, Postgres
 * changes stop being delivered for **the entire channel** — every other table
 * included. The channel still reports SUBSCRIBED, every binding still gets a
 * server-assigned id, and nothing errors. Nothing goes live, silently.
 *
 * That is not hypothetical: `collaborator_roster`, `collaborator_capabilities`,
 * `client_obligations` and `deliverable_feedback` were registered here but never
 * published, and the whole live-by-default contract was dead until 2026-07-29.
 * It hid because every write also does an optimistic `setQueryData`, so the
 * writer's own screen looked correct — only a second tab or a second device
 * would have shown the truth.
 *
 * When you add a key here, add the table to the publication in the same PR:
 *
 *   ALTER PUBLICATION supabase_realtime ADD TABLE public.<table>;
 *   ALTER TABLE public.<table> REPLICA IDENTITY FULL;   -- else UPDATE/DELETE
 *                                                       -- payloads carry only the PK
 * Verify with:
 *   SELECT c.relname FROM pg_publication_rel pr
 *     JOIN pg_publication p ON p.oid = pr.prpubid
 *     JOIN pg_class c ON c.oid = pr.prrelid
 *    WHERE p.pubname = 'supabase_realtime';
 *
 * See `supabase/migrations/20260729010000_realtime_publication_completes_tablemap.sql`.
 */
export const tableMap: Record<string, TableConfig> = {
  // Journey rows (the resolver's output). Status flips (pending→sent) and new
  // touches invalidate the booking's journey and the studio's booking list.
  scheduled_notifications: {
    filter: (userId) => `user_id=eq.${userId}`,
    invalidate: (payload, qc) => {
      const row = rowOf<{ user_id?: string; booking_id?: string }>(payload);
      if (!row) return;
      if (row.booking_id) {
        qc.invalidateQueries({ queryKey: queryKeys.journey(row.booking_id) });
      }
      if (row.user_id) {
        qc.invalidateQueries({ queryKey: queryKeys.journeyBookings(row.user_id) });
        qc.invalidateQueries({ queryKey: queryKeys.journeyQueue(row.user_id) });
      }
    },
  },

  // Both filter on the denormalized owner_user_id and invalidate the roster cache
  // (capabilities render together with the roster row).
  collaborator_roster: {
    filter: (userId) => `owner_user_id=eq.${userId}`,
    invalidate: (payload, qc) => {
      const row = rowOf<{ owner_user_id?: string }>(payload);
      if (row?.owner_user_id) {
        qc.invalidateQueries({ queryKey: queryKeys.collaboratorRoster(row.owner_user_id) });
      }
    },
  },

  collaborator_capabilities: {
    filter: (userId) => `owner_user_id=eq.${userId}`,
    invalidate: (payload, qc) => {
      const row = rowOf<{ owner_user_id?: string }>(payload);
      if (row?.owner_user_id) {
        qc.invalidateQueries({ queryKey: queryKeys.collaboratorRoster(row.owner_user_id) });
      }
    },
  },

  v2_brands: {
    filter: (userId) => `owner_user_id=eq.${userId}`,
    invalidate: (payload, qc) => {
      const row = rowOf<{ brand_id?: string; handle?: string; owner_user_id?: string }>(payload);
      if (!row) return;
      if (row.brand_id) {
        qc.invalidateQueries({ queryKey: queryKeys.brand(row.brand_id) });
        qc.invalidateQueries({ queryKey: queryKeys.brandTheme(row.brand_id) });
      }
      if (row.handle) {
        qc.invalidateQueries({ queryKey: queryKeys.brandByHandle(row.handle) });
        qc.invalidateQueries({ queryKey: queryKeys.publicBusinessLookup(row.handle) });
      }
      if (row.owner_user_id) {
        qc.invalidateQueries({ queryKey: queryKeys.brandLanguages(row.owner_user_id) });
      }
      if (row.owner_user_id) {
        qc.invalidateQueries({ queryKey: queryKeys.brands(row.owner_user_id) });
      }
    },
  },

  services: {
    filter: (userId) => `user_id=eq.${userId}`,
    invalidate: (payload, qc) => {
      const row = rowOf<{ user_id?: string }>(payload);
      if (!row?.user_id) return;
      qc.invalidateQueries({ queryKey: queryKeys.services(row.user_id) });
      // publicPackages has audience variants — partial-prefix invalidation
      // matches all of (userId), (userId, 'couples'), (userId, 'businesses'), etc.
      qc.invalidateQueries({ queryKey: [...queryKeys.all, 'public', 'packages', row.user_id] });
    },
  },

  // Pricing foundation inputs. A change to goals or expenses reprices every
  // Present-As preview and every public card — if an open tab misses it, that
  // tab shows dead prices (the 2833-vs-2874 incident, 2026-07-06). Both hooks
  // read shared queryKeys caches, so invalidation here reaches every surface.
  financial_goals: {
    filter: (userId) => `user_id=eq.${userId}`,
    invalidate: (payload, qc) => {
      const row = rowOf<{ user_id?: string }>(payload);
      if (row?.user_id) {
        qc.invalidateQueries({ queryKey: queryKeys.financialGoals(row.user_id) });
      }
    },
  },

  financial_expenses: {
    filter: (userId) => `user_id=eq.${userId}`,
    invalidate: (payload, qc) => {
      const row = rowOf<{ user_id?: string }>(payload);
      if (row?.user_id) {
        qc.invalidateQueries({ queryKey: queryKeys.financialExpenses(row.user_id) });
      }
    },
  },
  business_configuration: {
    filter: (userId) => `user_id=eq.${userId}`,
    invalidate: (payload, qc) => {
      const row = rowOf<{ user_id?: string }>(payload);
      if (!row?.user_id) return;
      qc.invalidateQueries({ queryKey: queryKeys.pricingConfig(row.user_id) });
      qc.invalidateQueries({ queryKey: queryKeys.publicPricingConfig(row.user_id) });
      // Phase 1: the portal service-standards card reads off this table.
      qc.invalidateQueries({ queryKey: queryKeys.serviceStandards(row.user_id) });
      // Studio: approve-before-send modes live in automation_rules.
      qc.invalidateQueries({ queryKey: queryKeys.journeyRules(row.user_id) });
      // The Payments card: stripe_account_state is written by the webhook, the
      // daily sweep and check-connect-status; the card renders the row.
      qc.invalidateQueries({ queryKey: queryKeys.connectAccountState(row.user_id) });
      // Bank transfers: the IBAN block on the same tab.
      qc.invalidateQueries({ queryKey: queryKeys.transferDetails(row.user_id) });
      // The whole row: Operations, and Profile's seller card (2026-10-03).
      qc.invalidateQueries({ queryKey: queryKeys.businessConfiguration(row.user_id) });
    },
  },

  // Payouts: one row per Stripe payout to the creator's bank, written by
  // stripe-webhook only. Published by
  // `20261001212255_stripe_account_state_and_payouts.sql`; the two ship together.
  payouts: {
    filter: (userId) => `user_id=eq.${userId}`,
    invalidate: (payload, qc) => {
      const row = rowOf<{ user_id?: string }>(payload);
      if (row?.user_id) {
        qc.invalidateQueries({ queryKey: queryKeys.payouts(row.user_id) });
      }
    },
  },

  // Business events: the creator's own feed, written by the edge only.
  // Published by `20261001222719_business_events.sql`; the two ship together.
  business_events: {
    filter: (userId) => `user_id=eq.${userId}`,
    invalidate: (payload, qc) => {
      const row = rowOf<{ user_id?: string }>(payload);
      if (row?.user_id) {
        qc.invalidateQueries({ queryKey: queryKeys.businessEvents(row.user_id) });
      }
    },
  },

  client_obligations: {
    filter: (userId) => `user_id=eq.${userId}`,
    invalidate: (payload, qc) => {
      const row = rowOf<{ user_id?: string; booking_id?: string }>(payload);
      if (row?.booking_id) {
        qc.invalidateQueries({ queryKey: queryKeys.obligations(row.booking_id) });
      }
      if (row?.user_id) {
        qc.invalidateQueries({ queryKey: queryKeys.obligationsForUser(row.user_id) });
      }
    },
  },

  // Phase 3: deliverable feedback (approval / revisions). Creator-owned rows
  // (RLS user_id=auth.uid()); the portal writes via the portal-feedback edge fn,
  // and those service-role writes echo back here to keep the creator queue live.
  deliverable_feedback: {
    filter: (userId) => `user_id=eq.${userId}`,
    invalidate: (payload, qc) => {
      const row = rowOf<{ user_id?: string; booking_id?: string }>(payload);
      if (row?.booking_id) {
        qc.invalidateQueries({ queryKey: queryKeys.deliverableFeedback(row.booking_id) });
      }
      if (row?.user_id) {
        qc.invalidateQueries({ queryKey: queryKeys.deliverableFeedbackForUser(row.user_id) });
      }
    },
  },

  // Deliverables: creator-owned rows (user_id), keyed per booking (project_id)
  // for the project manager and per creator for the load gauge. Published by
  // `20261001010419_deliverables_join_the_live_contract.sql`; the two must
  // ship together (an unpublished binding kills the whole channel).
  deliverables: {
    filter: (userId) => `user_id=eq.${userId}`,
    invalidate: (payload, qc) => {
      const row = rowOf<{ user_id?: string; project_id?: string }>(payload);
      if (row?.project_id) {
        qc.invalidateQueries({ queryKey: queryKeys.deliverables(row.project_id) });
      }
      if (row?.user_id) {
        qc.invalidateQueries({ queryKey: queryKeys.deliverablesForUser(row.user_id) });
      }
    },
  },

  // service_assignments doesn't have a direct user_id column — bookings do.
  // The supervisor still receives every change for assignments touching the
  // user's bookings (RLS filters on the read-side); we invalidate by booking_id
  // and let the assignments-by-brand query re-fetch any rows whose booking
  // belongs to the brand. Brand-level invalidation is delegated by listeners
  // through the booking_id mapping cache.
  service_assignments: {
    filter: () => '',
    invalidate: (payload, qc) => {
      const row = rowOf<{ booking_id?: string }>(payload);
      if (row?.booking_id) {
        qc.invalidateQueries({ queryKey: queryKeys.assignmentsForBooking(row.booking_id) });
      }
      // Brand-level dashboard view — partial prefix invalidation reaches every
      // assignments(brandId) cache without having to know which brand owns the booking.
      qc.invalidateQueries({ queryKey: [...queryKeys.all, 'assignments'] });
    },
  },

  // Crew asks — the availability cascade trail. Scoped to the creator via the
  // denormalized owner_user_id. An accepted ask also flips the slot, but that
  // echoes separately through service_assignments above.
  crew_asks: {
    filter: (userId) => `owner_user_id=eq.${userId}`,
    invalidate: (payload, qc) => {
      const row = rowOf<{ owner_user_id?: string }>(payload);
      if (row?.owner_user_id) {
        qc.invalidateQueries({ queryKey: queryKeys.crewAsks(row.owner_user_id) });
      }
      qc.invalidateQueries({ queryKey: [...queryKeys.all, 'assignments'] });
    },
  },

  // The creator's history (the import) and the payments read for it: the client
  // cards and Insights. Published by 20261016224009 (jobs, runs) and
  // 20261024121000 (payments), REPLICA IDENTITY FULL on all three; the bank-
  // statement import writes payments and runs from the browser.
  history_jobs: {
    filter: (userId) => `user_id=eq.${userId}`,
    invalidate: (payload, qc) => {
      const row = rowOf<{ user_id?: string }>(payload);
      if (row?.user_id) qc.invalidateQueries({ queryKey: queryKeys.historyJobs(row.user_id) });
    },
  },
  history_payments: {
    filter: (userId) => `user_id=eq.${userId}`,
    invalidate: (payload, qc) => {
      const row = rowOf<{ user_id?: string }>(payload);
      if (row?.user_id) qc.invalidateQueries({ queryKey: queryKeys.historyPayments(row.user_id) });
    },
  },
  // The price lists read from the creator's request PDFs (2026-11-02). Published
  // by 20261102180000; a run that loads them changes Insights and the client cards.
  history_price_lists: {
    filter: (userId) => `user_id=eq.${userId}`,
    invalidate: (payload, qc) => {
      const row = rowOf<{ user_id?: string }>(payload);
      if (row?.user_id) qc.invalidateQueries({ queryKey: queryKeys.historyPriceLists(row.user_id) });
    },
  },
  // The person's marks of their content units ("Mark as posted", THE_LIST #66
  // step 4). Published by `20261101190000_content_marks.sql`; applied and
  // registered here in the same move (2026-11-01).
  content_marks: {
    filter: (userId) => `user_id=eq.${userId}`,
    invalidate: (payload, qc) => {
      const row = rowOf<{ user_id?: string }>(payload);
      if (row?.user_id) qc.invalidateQueries({ queryKey: queryKeys.contentMarks(row.user_id) });
    },
  },
  // The creator's saved shelf on the Content tab (THE_LIST #62). Published by
  // `20261102141500_saved_references.sql`, REPLICA IDENTITY FULL. WRITTEN BEFORE
  // THAT MIGRATION IS APPLIED: apply it first, because a table named here and
  // missing from the publication rejects the whole channel for every screen
  // (2026-07-29). One import adds hundreds of rows, so the refresh waits for the
  // echoes to stop.
  saved_references: {
    filter: (userId) => `user_id=eq.${userId}`,
    invalidate: (payload, qc) => {
      const row = rowOf<{ user_id?: string }>(payload);
      if (row?.user_id) invalidateAfterQuiet(qc, queryKeys.savedReferences(row.user_id));
    },
  },

  // The colleague rate card and the requests on it. Published by
  // `20261102150000_peer_rate_cards_and_gigs.sql`, REPLICA IDENTITY FULL on both.
  // THAT MIGRATION GOES LIVE BEFORE THESE TWO LINES REACH A BROWSER (see the warning
  // above this map). A gig is written by peer-gig with the service role; the echo
  // here is what puts a new request on the creator's screen.
  peer_rate_cards: {
    filter: (userId) => `creator_user_id=eq.${userId}`,
    invalidate: (payload, qc) => {
      const row = rowOf<{ creator_user_id?: string }>(payload);
      if (row?.creator_user_id) qc.invalidateQueries({ queryKey: queryKeys.peerRateCard(row.creator_user_id) });
    },
  },
  peer_gigs: {
    filter: (userId) => `creator_user_id=eq.${userId}`,
    invalidate: (payload, qc) => {
      const row = rowOf<{ creator_user_id?: string }>(payload);
      if (row?.creator_user_id) qc.invalidateQueries({ queryKey: queryKeys.peerGigs(row.creator_user_id) });
    },
  },

  // A run that finishes means jobs or payments changed with it.
  history_import_runs: {
    filter: (userId) => `user_id=eq.${userId}`,
    invalidate: (payload, qc) => {
      const row = rowOf<{ user_id?: string }>(payload);
      if (row?.user_id) {
        qc.invalidateQueries({ queryKey: queryKeys.historyJobs(row.user_id) });
        qc.invalidateQueries({ queryKey: queryKeys.historyPayments(row.user_id) });
      }
    },
  },
};
